/**
 * The "Overrides" section of the sliding job launch drawer.
 * Let the user specify overrides (args, config, entry point) for
 * what we are launching.
 */

import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';

import {useSecretsQuery} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {JobRun} from '../JobDetail/utilTypes';
import {FILE_KEY_DELIMITER, JsonObject, Property} from '../utilTypes';
import {Dropdown} from './Overrides/Dropdown';
import {NestedOverrides} from './Overrides/NestedOverrides';
import {SecretDropdown} from './Overrides/SecretDropdown';
import {TextEntry} from './Overrides/TextEntry';
import {DOT, propertyMapToList} from './Overrides/util';

type JobDrawerStructuredOverridesProps = {
  overrides: JsonObject;
  fileOverrides?: JsonObject;
  onStructuredOverridesChange: (config: JsonObject, files?: JsonObject) => void;
  onSelectRun: (run: JobRun | null) => void;
  schemas: JsonObject;
  onSecretsStateChange?: (loading: boolean, error: boolean) => void;
};

function getProperties(schemas: JsonObject) {
  const allProperties: Property[] = [];
  const configProps =
    ((schemas['@wandb.config'] as JsonObject)?.properties as any) ?? {};
  allProperties.push(...propertyMapToList(configProps, null, null));
  for (const [file, schema] of Object.entries(schemas.files ?? {})) {
    const props = schema.properties ?? {};
    allProperties.push(...propertyMapToList(props, null, file));
  }
  return allProperties;
}

function getNestedOverrideValue(overrides: JsonObject, key: string) {
  let root: any = overrides;
  const remainingKeys = key.split(DOT);
  while (remainingKeys.length > 1) {
    const idx = remainingKeys.shift() as string;
    if (root === null || root[idx] == null) {
      return null;
    }
    root = root[idx];
  }
  return root[remainingKeys[0].replaceAll('\\.', '.')];
}

function setOverrideValue(overrides: JsonObject, key: string, value: any) {
  let root: any = overrides;
  const remainingKeys = key.split(DOT);
  while (remainingKeys.length > 1) {
    const idx = remainingKeys.shift() as string;
    if (root[idx] == null) {
      root[idx] = {};
    }
    root = root[idx];
  }
  root[remainingKeys[0].replaceAll('\\.', '.')] = value;
}

export const JobDrawerStructuredOverrides = ({
  overrides,
  fileOverrides,
  onStructuredOverridesChange,
  onSelectRun,
  schemas,
  onSecretsStateChange,
}: JobDrawerStructuredOverridesProps) => {
  const [properties, setProperties] = useState<Property[]>([]);

  const onTextEntryChange = (event: any, context: any) => {
    const [file, key] = context.id.split(FILE_KEY_DELIMITER);
    const newOverrides: JsonObject = overrides;
    const newFiles: JsonObject | undefined = fileOverrides;
    const newValue = context.type === 'number' ? +context.value : context.value;
    if (file && newFiles && newFiles[file]) {
      setOverrideValue(newFiles[file] as JsonObject, key, newValue);
    } else {
      setOverrideValue(newOverrides, key, newValue);
    }
    onStructuredOverridesChange(newOverrides, newFiles);
    onSelectRun(null);
  };

  const onDropdownChange = (newValue: any, actionMeta: any) => {
    const [file, key] = newValue.property.split(FILE_KEY_DELIMITER);
    const newOverrides: JsonObject = overrides;
    const newFiles: JsonObject | undefined = fileOverrides;
    if (file && newFiles && newFiles[file]) {
      setOverrideValue(newFiles[file] as JsonObject, key, newValue.value);
    } else {
      setOverrideValue(newOverrides, key, newValue.value);
    }
    onStructuredOverridesChange(newOverrides, newFiles);
    onSelectRun(null);
  };

  useEffect(() => {
    setProperties(getProperties(schemas));
  }, [schemas]);

  const viewer = useViewer();
  const {loading, data, error} = useSecretsQuery({
    variables: {
      entityName: viewer?.entity || '',
    },
    skip: !viewer || !viewer.entity,
  });

  useEffect(() => {
    onSecretsStateChange?.(loading, !!error);
  }, [loading, error, onSecretsStateChange]);

  const getOverrideValue = (property: Property) => {
    if (property.file && fileOverrides && fileOverrides[property.file]) {
      return getNestedOverrideValue(
        fileOverrides[property.file] as JsonObject,
        property.key
      );
    }
    return getNestedOverrideValue(overrides, property.key);
  };

  const getElementForProperty = (property: Property) => {
    if (
      ['string', 'integer', 'number'].indexOf(property.type) > -1 &&
      !property.enum
    ) {
      if (property.format === 'secret') {
        return SecretDropdown({
          secrets: data?.entity?.secrets ?? [],
          property,
          value: (getOverrideValue(property) as string) || null,
          onChange: onDropdownChange,
          loading,
          error: !!error,
        });
      }
      // string/number/int without enum: Freeform text entry
      return TextEntry({
        property,
        value: getOverrideValue(property) as string | number,
        onChange: onTextEntryChange,
      });
    } else if (
      (['string', 'integer', 'number'].indexOf(property.type) > -1 &&
        property.enum) ||
      property.type === 'boolean'
    ) {
      // string/number/int with enum: Dropdown
      // bool: Dropdown with true/false
      return Dropdown({
        property,
        value: getOverrideValue(property) as string | number | boolean,
        onChange: onDropdownChange,
      });
    } else if (
      property.type === 'array' &&
      property.items &&
      Array.isArray(property.items.enum)
    ) {
      // array of enum: Multi-select dropdown
      return Dropdown({
        property,
        value: (getOverrideValue(property) as (string | number)[]) ?? [],
        onChange: onDropdownChange,
      });
    } else if (property.type === 'object' && property.properties) {
      // object: Recursively group properties
      return NestedOverrides({
        property,
        getElementForProperty,
      });
    }
    return null;
  };

  return (
    <Tailwind>
      <div className="mt-10 w-full rounded border border-moon-250 bg-white p-12">
        {properties.map((property, idx, arr) => {
          return (
            <>
              {getElementForProperty(property)}

              {idx < arr.length - 1 && (
                <div className="m-[20px_-12px] h-px w-auto bg-moon-250" />
              )}
            </>
          );
        })}
      </div>
    </Tailwind>
  );
};
